'use client';

/**
 * Reclipt marketing UI primitives — Stitch "Void & Neon" system.
 * Colours/glows/fonts come from the `mk` Tailwind tokens — no scattered hex.
 */

import Link from 'next/link';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import { useEffect, useRef, type ReactNode } from 'react';

/** Uppercase, wide-tracked Space-Grotesk label that sits above section headings. */
export function Label({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={`font-mk-label text-xs font-bold uppercase tracking-[0.18em] text-mk-pink ${className}`}
    >
      {children}
    </p>
  );
}

/** Massive Bebas Neue display heading. */
export function Display({
  children,
  className = '',
  as: Tag = 'h2',
}: {
  children: ReactNode;
  className?: string;
  as?: 'h1' | 'h2' | 'h3';
}) {
  return (
    <Tag
      className={`font-mk-display uppercase leading-[0.92] tracking-tight text-mk-ink ${className}`}
    >
      {children}
    </Tag>
  );
}

/** Tight, heavy Inter headline (used for sub-headlines / step titles). */
export function Headline({
  children,
  className = '',
  as: Tag = 'h2',
}: {
  children: ReactNode;
  className?: string;
  as?: 'h2' | 'h3' | 'h4';
}) {
  return (
    <Tag
      className={`font-mk-body font-extrabold tracking-[-0.03em] text-mk-ink ${className}`}
    >
      {children}
    </Tag>
  );
}

/** Glass card: translucent white + heavy blur + 1px hairline + hyper-rounded. */
export function GlassCard({
  children,
  className = '',
  hover = false,
}: {
  children: ReactNode;
  className?: string;
  hover?: boolean;
}) {
  return (
    <div
      className={[
        'rounded-3xl border border-white/10 bg-white/[0.05] backdrop-blur-2xl',
        hover ? 'transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.08]' : '',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

/** Primary button — solid electric-pink fill, white text, ambient neon glow. */
export function PinkButton({
  href,
  children,
  className = '',
  size = 'md',
}: {
  href: string;
  children: ReactNode;
  className?: string;
  size?: 'md' | 'lg';
}) {
  const pad = size === 'lg' ? 'px-10 py-5 text-lg' : 'px-6 py-3 text-sm';
  return (
    <Link
      href={href}
      className={`inline-flex items-center justify-center gap-2 rounded-xl bg-mk-pink font-mk-body font-bold text-mk-on-pink shadow-mk-neon transition-transform duration-200 hover:scale-105 active:scale-95 ${pad} ${className}`}
    >
      {children}
    </Link>
  );
}

/** Secondary button — glass style. */
export function GlassButton({
  href,
  children,
  className = '',
  size = 'md',
}: {
  href: string;
  children: ReactNode;
  className?: string;
  size?: 'md' | 'lg';
}) {
  const pad = size === 'lg' ? 'px-10 py-5 text-lg' : 'px-6 py-3 text-sm';
  return (
    <Link
      href={href}
      className={`inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] font-mk-body font-bold text-mk-ink backdrop-blur-2xl transition-colors duration-200 hover:bg-white/10 ${pad} ${className}`}
    >
      {children}
    </Link>
  );
}

/** Fade/slide-in-on-scroll reveal. */
export function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/** Animated rolling count-up number, triggers on enter-viewport. */
export function CountUp({
  to,
  duration = 1.6,
  className = '',
}: {
  to: number;
  duration?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: duration * 1000, bounce: 0 });

  useEffect(() => {
    if (inView) mv.set(to);
  }, [inView, to, mv]);

  useEffect(() => {
    return spring.on('change', (v) => {
      if (ref.current) ref.current.textContent = Math.round(v).toLocaleString();
    });
  }, [spring]);

  return (
    <span ref={ref} className={className}>
      0
    </span>
  );
}
